import React, { useState } from "react";
import { Alert, Box, Button, Chip, Drawer, Stack, Typography } from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { DripEnrollment } from "../../../types/marketing";
import { ReconcileEmailSend } from "./ReconcileEmailSend";
import { EmailPreview } from "./EmailPreview";
import { emailError, type SendOutcome } from "./emailWorkflows";
interface Timeline {
  enrollment: DripEnrollment;
  campaign_active: boolean;
  steps: {
    id: number;
    order: number;
    template_name: string;
    subject: string;
    html_content: string;
    cumulative_hours: number;
    status: string;
    send: SendOutcome | null;
  }[];
}
export function DripEnrollmentDetails({
  id,
  onClose,
}: {
  id: number;
  onClose: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const cache = useQueryClient();
  const timeline = useQuery({
    queryKey: ["email-drip-timeline", id],
    queryFn: async () =>
      (
        await api.get<Timeline>(
          `/api/marketing/drip-enrollments/${id}/timeline/`,
        )
      ).data,
    refetchInterval: 15000,
  });
  const run = async (action: "retry" | "cancel") => {
    if (
      action === "cancel" &&
      !window.confirm("Cancel remaining steps for this contact?")
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/marketing/drip-enrollments/${id}/${action}/`, {});
      await timeline.refetch();
      await cache.invalidateQueries({ queryKey: ["drip-enrollments"] });
    } catch (cause) {
      setError(emailError(cause));
    }
    setBusy(false);
  };
  return (
    <Drawer
      open
      anchor="right"
      onClose={onClose}
      size="lg"
      slotProps={{
        content: {
          sx: { width: { xs: "100vw", sm: 650 }, p: 2, overflowY: "auto" },
        },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h4">Enrollment progress</Typography>
          <Button variant="plain" onClick={onClose}>
            Close
          </Button>
        </Stack>
        {error && <Alert color="danger">{error}</Alert>}
        {timeline.isError && (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => timeline.refetch()}>Retry</Button>
            }
          >
            Timeline unavailable.
          </Alert>
        )}
        {timeline.isPending && <Typography>Loading timeline…</Typography>}
        {timeline.data && (
          <>
            <Typography level="title-md">
              {timeline.data.enrollment.contact_name} ·{" "}
              {timeline.data.enrollment.contact_email}
            </Typography>
            <Typography>
              {timeline.data.enrollment.drip_campaign_name} ·{" "}
              {timeline.data.enrollment.status}
            </Typography>
            {!timeline.data.campaign_active && (
              <Alert color="warning">
                Sequence inactive. Outstanding steps are held.
              </Alert>
            )}
            <Typography level="body-sm">
              Next attempt:{" "}
              {timeline.data.enrollment.next_send_at
                ? new Date(
                    timeline.data.enrollment.next_send_at,
                  ).toLocaleString()
                : "None scheduled"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                disabled={
                  busy ||
                  !timeline.data.campaign_active ||
                  timeline.data.enrollment.status !== "active"
                }
                onClick={() => run("retry")}
              >
                Retry failed or quota-held step
              </Button>
              <Button
                variant="plain"
                color="danger"
                disabled={busy || timeline.data.enrollment.status !== "active"}
                onClick={() => run("cancel")}
              >
                Cancel enrollment
              </Button>
            </Stack>
            {timeline.data.steps.map((step) => (
              <Box
                key={step.id}
                sx={{
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: step.send?.sent_at
                    ? "success.400"
                    : "neutral.300",
                }}
              >
                <Stack direction="row" spacing={1}>
                  <Typography level="title-md">
                    {step.order + 1}. {step.template_name}
                  </Typography>
                  <Chip size="sm">{step.status}</Chip>
                </Stack>
                <Typography level="body-sm">
                  Baseline timing: {step.cumulative_hours} hours from
                  enrollment; holds extend later steps.
                </Typography>
                {step.send && (
                  <>
                    <Typography level="body-sm">
                      {step.send.attempts} attempts ·{" "}
                      {step.send.sent_at
                        ? `Accepted ${new Date(step.send.sent_at).toLocaleString()}`
                        : "Acceptance not confirmed"}
                    </Typography>
                    {step.send.error && (
                      <Alert
                        color={
                          step.status === "uncertain" ? "warning" : "neutral"
                        }
                      >
                        {step.send.error}
                      </Alert>
                    )}
                  </>
                )}
                {step.send &&
                  ["uncertain", "dispatching"].includes(step.status) && (
                    <Box component="details">
                      <Box component="summary" sx={{ cursor: "pointer" }}>
                        Reconcile provider outcome
                      </Box>
                      <ReconcileEmailSend
                        send={step.send}
                        onComplete={() => {
                          void timeline.refetch();
                          void cache.invalidateQueries({
                            queryKey: ["email-quota"],
                          });
                        }}
                      />
                    </Box>
                  )}
                <Box component="details">
                  <Box component="summary" sx={{ cursor: "pointer", my: 1 }}>
                    Preview step
                  </Box>
                  <EmailPreview
                    html={step.html_content}
                    subject={step.subject}
                    sampleControls={false}
                  />
                </Box>
              </Box>
            ))}
          </>
        )}
      </Stack>
    </Drawer>
  );
}
